import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { API } from '../../context/AuthContext';
import { useAuth } from '../../context/AuthContext';
import {
  Building2,
  Phone,
  Mail,
  Globe,
  MapPin,
  MapPinned,
  Calendar,
  Users,
  UserCircle,
  Briefcase,
  Target,
  Flag,
  Eye,
  CheckCircle2,
  BookOpen,
  FileText,
  Image,
  Download,
  Award,
  Shield,
  Share2,
  ExternalLink,
  ChevronRight,
  Edit,
  Pencil,
  Heart,
  Activity,
  DollarSign,
  Clock,
  Tag,
  Hash,
  UserCheck,
  BadgeCheck,
  Camera,
  Play,
  Layout,
  Layers,
  Trash2
} from 'lucide-react';

function InfoRow({ icon: IconComponent, label, value }) {
  if (!value) return null;
  return (
    <div style={{ display:'flex', alignItems:'flex-start', gap:10, padding:'10px 0', borderBottom:'1px solid var(--gray-100)' }}>
      <span style={{ width:20, textAlign:'center', flexShrink: 0 }}>
        <IconComponent size={16} />
      </span>
      <div style={{ flex:1 }}>
        <div style={{ fontSize:'0.72rem', color:'var(--gray-400)', textTransform:'uppercase', letterSpacing:'0.5px', fontWeight:600 }}>{label}</div>
        <div style={{ fontSize:'0.85rem', color:'var(--gray-700)', marginTop:2 }}>{value}</div>
      </div>
    </div>
  );
}

export default function OrgDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cso, setCso] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [certifying, setCertifying] = useState(false);

  useEffect(() => {
    API.get(`/csos/id/${id}`)
      .then(r => setCso(r.data))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleCertify = async () => {
    setCertifying(true);
    try {
      await API.patch(`/csos/${id}/certify`, { certify: !cso.zan_certified });
      setCso(c => ({ ...c, zan_certified: !c.zan_certified }));
      toast.success(cso.zan_certified ? 'Certification removed' : 'CSO Certified');
    } catch { toast.error('Failed'); }
    finally { setCertifying(false); }
  };

  if (loading) return <div className="loading-center"><div className="spin"/></div>;
  if (!cso) return <div className="empty-state"><h3>Organisation not found</h3></div>;

  const canEdit = ['super_admin','admin','data_entry'].includes(user?.role);
  const canCertify = ['super_admin','admin','field_officer'].includes(user?.role);
  const photos = cso.media?.filter(m => m.media_type === 'photo') || [];
  const docs   = cso.media?.filter(m => m.media_type === 'document') || [];

  const tabs = [
    { key:'overview',  icon: Layout, label:'Overview' },
    { key:'programs',  icon: Target, label:`Programs & Projects (${cso.programs?.length||0})` },
    { key:'focus',     icon: Layers, label:'Focus Areas' },
    { key:'gallery',   icon: Image, label:'Gallery' },
    { key:'documents', icon: FileText, label:'Documents' },
    { key:'contacts',  icon: Phone, label:'Contacts' },
    { key:'board',     icon: Users, label:'Board / Directors' },
  ];

  return (
    <div className="anim-fadeIn">
      {/* Breadcrumb */}
      <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:'0.8rem', color:'var(--gray-400)', marginBottom:16 }}>
        <Link to="/admin/organisations" style={{ color:'var(--gray-400)' }}>Organisations</Link>
        <ChevronRight size={14} />
        <span style={{ color:'var(--gray-700)' }}>{cso.org_name}</span>
      </div>

      {/* Profile Header — matches Image 2 */}
      <div className="card" style={{ marginBottom:24, overflow:'hidden' }}>
        {/* Banner */}
        <div className="cso-profile-banner">
          {cso.banner_url && <img src={`${cso.banner_url}`} alt="banner"/>}
        </div>

        {/* Info row */}
        <div className="cso-profile-info">
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
            <div style={{ display:'flex', alignItems:'flex-end', gap:16 }}>
              <div className="cso-profile-logo">
                {cso.logo_url
                  ? <img src={`${cso.logo_url}`} alt={cso.org_name}/>
                  : (cso.acronym||cso.org_name).slice(0,2).toUpperCase()}
              </div>
              <div style={{ paddingBottom:4 }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                  <h1 style={{ fontSize:'1.4rem', fontWeight:800 }}>{cso.org_name}</h1>
                  {cso.zan_certified && (
                    <span style={{ display:'flex', alignItems:'center', gap:5, background:'#e8f5e9', color:'#2e7d32', padding:'3px 10px', borderRadius:20, fontSize:'0.78rem', fontWeight:700, border:'1px solid #c8e6c9' }}>
                      <BadgeCheck size={16} /> Verified
                    </span>
                  )}
                </div>
                {cso.org_type && <div style={{ fontSize:'0.83rem', color:'var(--gray-500)', marginTop:2 }}>{cso.org_type}</div>}
                <div style={{ display:'flex', alignItems:'center', gap:16, marginTop:8, flexWrap:'wrap', fontSize:'0.8rem', color:'var(--gray-500)' }}>
                  {cso.year_established && <span style={{ display:'flex', alignItems:'center', gap:4 }}><Calendar size={14} /> Established: {cso.year_established}</span>}
                  {cso.reg_number && <span style={{ display:'flex', alignItems:'center', gap:4 }}><Hash size={14} /> {cso.reg_type||'Reg'} Number: {cso.reg_number}</span>}
                  {cso.province && <span style={{ display:'flex', alignItems:'center', gap:4 }}><MapPin size={14} /> {cso.province} Province</span>}
                  {cso.district && <span style={{ display:'flex', alignItems:'center', gap:4 }}><MapPin size={14} /> {cso.district} District</span>}
                </div>
                {/* Social links */}
                <div style={{ display:'flex', gap:8, marginTop:10 }}>
                  {cso.website   && <a href={cso.website}   target="_blank" rel="noreferrer" style={{ fontSize:'1.1rem' }}><Globe size={18} /></a>}
                  {cso.facebook  && <a href={cso.facebook}  target="_blank" rel="noreferrer" style={{ fontSize:'1.1rem' }}><Globe size={18} /></a>}
                  {cso.x_twitter && <a href={cso.x_twitter} target="_blank" rel="noreferrer" style={{ fontSize:'1.1rem' }}><Globe size={18} /></a>}
                  {cso.linkedin  && <a href={cso.linkedin}  target="_blank" rel="noreferrer" style={{ fontSize:'1.1rem' }}><Globe size={18} /></a>}
                  {cso.youtube   && <a href={cso.youtube}   target="_blank" rel="noreferrer" style={{ fontSize:'1.1rem' }}><Globe size={18} /></a>}
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div style={{ display:'flex', flexDirection:'column', gap:8, paddingTop:16 }}>
              {canEdit && (
                <Link to={`/admin/organisations/edit/${id}`} className="btn btn-primary">
                  <Edit size={16} style={{ marginRight: 6 }} /> Edit Organisation
                </Link>
              )}
              <a href={`/csos/${cso.slug}`} target="_blank" rel="noreferrer" className="btn btn-secondary">
                <ExternalLink size={16} style={{ marginRight: 6 }} /> View Public Profile
              </a>
              {canCertify && (
                <button onClick={handleCertify} disabled={certifying}
                  className={`btn ${cso.zan_certified ? 'btn-secondary' : 'btn-primary'}`}
                  style={cso.zan_certified ? { background:'#fff7ed', color:'#9a3412', border:'1px solid #fed7aa' } : {}}>
                  {certifying ? '...' : cso.zan_certified ? (
                    <><Shield size={16} style={{ marginRight: 6 }} /> Remove Certification</>
                  ) : (
                    <><Award size={16} style={{ marginRight: 6 }} /> Certify Organisation</>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tab-list">
        {tabs.map(t => {
          const TabIcon = t.icon;
          return (
            <button key={t.key} className={`tab-btn${activeTab===t.key?' active':''}`} onClick={() => setActiveTab(t.key)}>
              <TabIcon size={16} style={{ marginRight: 6 }} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div className="anim-fadeIn">

        {/* ── OVERVIEW ── */}
        {activeTab==='overview' && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 280px', gap:20, alignItems:'start' }}>
            <div>
              {/* About */}
              <div className="card card-pad" style={{ marginBottom:16 }}>
                <h3 style={{ fontSize:'0.95rem', fontWeight:700, marginBottom:16 }}>About Organisation</h3>
                {cso.mission && (
                  <div style={{ marginBottom:14 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                      <Flag size={18} /><strong style={{ fontSize:'0.85rem', color:'var(--green)' }}>Mission</strong>
                    </div>
                    <p style={{ fontSize:'0.85rem', color:'var(--gray-600)', lineHeight:1.7, marginLeft:26 }}>{cso.mission}</p>
                  </div>
                )}
                {cso.vision && (
                  <div style={{ marginBottom:14 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                      <Eye size={18} /><strong style={{ fontSize:'0.85rem', color:'var(--green)' }}>Vision</strong>
                    </div>
                    <p style={{ fontSize:'0.85rem', color:'var(--gray-600)', lineHeight:1.7, marginLeft:26 }}>{cso.vision}</p>
                  </div>
                )}
                {cso.objectives && (
                  <div>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                      <CheckCircle2 size={18} /><strong style={{ fontSize:'0.85rem', color:'var(--green)' }}>Objectives</strong>
                    </div>
                    <div style={{ marginLeft:26 }}>
                      {cso.objectives.split('\n').filter(Boolean).map((obj,i) => (
                        <div key={i} style={{ display:'flex', gap:8, marginBottom:4, fontSize:'0.83rem', color:'var(--gray-600)' }}>
                          <span style={{ color:'var(--green)', flexShrink:0 }}>•</span>{obj}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {cso.background && (
                  <div style={{ marginTop:14 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                      <BookOpen size={18} /><strong style={{ fontSize:'0.85rem', color:'var(--green)' }}>Background</strong>
                    </div>
                    <p style={{ fontSize:'0.85rem', color:'var(--gray-600)', lineHeight:1.7, marginLeft:26 }}>{cso.background}</p>
                  </div>
                )}
              </div>

              {/* Key Information */}
              <div className="card card-pad" style={{ marginBottom:16 }}>
                <h3 style={{ fontSize:'0.95rem', fontWeight:700, marginBottom:12 }}>Key Information</h3>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 24px' }}>
                  <InfoRow icon={Hash} label="Registration No." value={cso.reg_number}/>
                  <InfoRow icon={FileText} label="Legal Status" value={cso.legal_status}/>
                  <InfoRow icon={Calendar} label="Operating Since" value={cso.year_established}/>
                  <InfoRow icon={Users} label="Staff Strength" value={cso.staff_strength}/>
                  <InfoRow icon={DollarSign} label="Annual Budget" value={cso.annual_budget}/>
                  <InfoRow icon={Heart} label="Main Donors" value={cso.main_donors}/>
                  <InfoRow icon={Activity} label="Operational Status" value={cso.operational_status}/>
                  <InfoRow icon={Building2} label="Organisation Type" value={cso.org_type}/>
                </div>
              </div>

              {/* Programs preview */}
              {cso.programs?.length > 0 && (
                <div className="card card-pad">
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:14 }}>
                    <h3 style={{ fontSize:'0.95rem', fontWeight:700 }}>Programs & Projects ({cso.programs.length})</h3>
                    <button className="btn btn-sm btn-secondary" onClick={() => setActiveTab('programs')}>View All</button>
                  </div>
                  <div className="grid-2" style={{ gap:12 }}>
                    {cso.programs.slice(0,4).map(p => (
                      <div key={p.id} style={{ padding:'12px 14px', background:'var(--gray-50)', borderRadius:8, border:'1px solid var(--gray-200)' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                          <Target size={18} />
                          <strong style={{ fontSize:'0.83rem' }}>{p.program_name}</strong>
                          <span className={`badge ${p.status==='active'?'badge-green':'badge-gray'}`} style={{ fontSize:'0.65rem', marginLeft:'auto' }}>{p.status}</span>
                        </div>
                        {p.description && <p style={{ fontSize:'0.78rem', color:'var(--gray-500)', lineHeight:1.5 }}>{p.description.slice(0,100)}{p.description.length>100?'...':''}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Thematic Areas */}
              {cso.thematic_area_ids?.length > 0 && (
                <div className="card card-pad" style={{ marginTop:16 }}>
                  <h3 style={{ fontSize:'0.95rem', fontWeight:700, marginBottom:12 }}>Thematic Areas</h3>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                    {cso.thematic_area_ids.map((id,i) => <span key={i} className="tag">{id}</span>)}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div>
              {/* Contact */}
              <div className="card card-pad" style={{ marginBottom:14 }}>
                <h4 style={{ fontSize:'0.85rem', fontWeight:700, marginBottom:12 }}>Contact Information</h4>
                {cso.phone && <div style={{ display:'flex', gap:8, marginBottom:8, fontSize:'0.82rem', color:'var(--gray-600)' }}><Phone size={16} />{cso.phone}</div>}
                {cso.email && <div style={{ display:'flex', gap:8, marginBottom:8, fontSize:'0.82rem', color:'var(--gray-600)' }}><Mail size={16} />{cso.email}</div>}
                {cso.website && <div style={{ display:'flex', gap:8, marginBottom:8, fontSize:'0.82rem' }}><Globe size={16} /><a href={cso.website} target="_blank" rel="noreferrer" style={{ color:'var(--green)' }}>{cso.website}</a></div>}
                {cso.physical_address && <div style={{ display:'flex', gap:8, fontSize:'0.82rem', color:'var(--gray-600)', lineHeight:1.5 }}><MapPin size={16} style={{ flexShrink:0 }} />{cso.physical_address}</div>}
              </div>

              {/* Location map placeholder */}
              {(cso.gps_lat || cso.province) && (
                <div className="card card-pad" style={{ marginBottom:14 }}>
                  <h4 style={{ fontSize:'0.85rem', fontWeight:700, marginBottom:10 }}>Location on Map</h4>
                  <div className="map-placeholder" style={{ height:140 }}>
                    <MapPinned size={24} style={{ marginBottom: 8 }} />
                    <div>{cso.province}{cso.district?`, ${cso.district}`:''}</div>
                    {cso.gps_lat && <div style={{ fontSize:'0.7rem', marginTop:4 }}>{cso.gps_lat}, {cso.gps_lng}</div>}
                  </div>
                </div>
              )}

              {/* Areas of operation */}
              {cso.areas_of_operation?.length > 0 && (
                <div className="card card-pad" style={{ marginBottom:14 }}>
                  <h4 style={{ fontSize:'0.85rem', fontWeight:700, marginBottom:10 }}>Areas of Operation</h4>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
                    {cso.areas_of_operation.map((a,i) => <span key={i} className="badge badge-gray" style={{ fontSize:'0.72rem' }}>{a.name}</span>)}
                  </div>
                </div>
              )}

              {/* Social media stats */}
              {(cso.facebook_followers||cso.x_followers||cso.linkedin_followers||cso.youtube_subscribers) && (
                <div className="card card-pad" style={{ marginBottom:14 }}>
                  <h4 style={{ fontSize:'0.85rem', fontWeight:700, marginBottom:12 }}>Social Media</h4>
                  {[
                    { label:'Followers', platform:'Facebook', val:cso.facebook_followers },
                    { label:'Followers', platform:'X (Twitter)', val:cso.x_followers },
                    { label:'Followers', platform:'LinkedIn', val:cso.linkedin_followers },
                    { label:'Subscribers', platform:'YouTube', val:cso.youtube_subscribers },
                  ].filter(s=>s.val).map((s,i) => (
                    <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom:'1px solid var(--gray-100)', fontSize:'0.8rem' }}>
                      <span style={{ color:'var(--gray-600)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Share2 size={16} /> {s.platform}
                      </span>
                      <strong>{parseInt(s.val).toLocaleString()} {s.label}</strong>
                    </div>
                  ))}
                </div>
              )}

              {/* Memberships */}
              <div className="card card-pad">
                <h4 style={{ fontSize:'0.85rem', fontWeight:700, marginBottom:10 }}>Memberships</h4>
                <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                  {cso.member_zan     && <span className="badge badge-green"><CheckCircle2 size={14} style={{ marginRight: 4 }} />ZAN</span>}
                  {cso.member_nac     && <span className="badge badge-green"><CheckCircle2 size={14} style={{ marginRight: 4 }} />NAC</span>}
                  {cso.member_nango   && <span className="badge badge-green"><CheckCircle2 size={14} style={{ marginRight: 4 }} />NANGO</span>}
                  {cso.member_mopslsw && <span className="badge badge-green"><CheckCircle2 size={14} style={{ marginRight: 4 }} />MoPSLSW</span>}
                  {cso.member_other   && <span className="badge badge-gray">{cso.member_other}</span>}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── PROGRAMS ── */}
        {activeTab==='programs' && (
          <div>
            {cso.programs?.length===0
              ? <div className="empty-state">
                  <div className="empty-state-icon"><Target size={48} /></div>
                  <h3>No programs listed</h3>
                </div>
              : <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                  {cso.programs.map(p => (
                    <div key={p.id} className="card card-pad" style={{ borderLeft:`4px solid ${p.status==='active'?'var(--green)':p.status==='completed'?'var(--gray-300)':'var(--gold)'}`}}>
                      <div style={{ display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
                        <div style={{ flex:1 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
                            <Target size={20} />
                            <h4 style={{ fontSize:'0.95rem' }}>{p.program_name}</h4>
                          </div>
                          {p.description && <p style={{ fontSize:'0.83rem', color:'var(--gray-600)', lineHeight:1.6, marginBottom:8 }}>{p.description}</p>}
                          <div style={{ display:'flex', gap:14, flexWrap:'wrap', fontSize:'0.78rem', color:'var(--gray-500)' }}>
                            {p.beneficiaries && <span style={{ display:'flex', alignItems:'center', gap:4 }}><Users size={14} /> {p.beneficiaries}</span>}
                            {p.donor && <span style={{ display:'flex', alignItems:'center', gap:4 }}><DollarSign size={14} /> {p.donor}</span>}
                            {p.budget && <span style={{ display:'flex', alignItems:'center', gap:4 }}><DollarSign size={14} /> {p.budget}</span>}
                            {p.start_date && <span style={{ display:'flex', alignItems:'center', gap:4 }}><Calendar size={14} /> {new Date(p.start_date).getFullYear()}{p.end_date?` – ${new Date(p.end_date).getFullYear()}`:' – Present'}</span>}
                          </div>
                        </div>
                        <span className={`badge ${p.status==='active'?'badge-green':p.status==='completed'?'badge-gray':'badge-yellow'}`} style={{ alignSelf:'flex-start' }}>{p.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
            }
          </div>
        )}

        {/* ── GALLERY ── */}
        {activeTab==='gallery' && (
          <div>
            {photos.length===0
              ? <div className="empty-state">
                  <div className="empty-state-icon"><Image size={48} /></div>
                  <h3>No photos uploaded</h3>
                </div>
              : <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:12 }}>
                  {photos.map(photo => (
                    <div key={photo.id} style={{ position:'relative', borderRadius:8, overflow:'hidden', aspectRatio:'4/3', background:'var(--gray-100)', cursor:'pointer' }}>
                      <img src={`${photo.file_url}`} alt={photo.title||''} style={{ width:'100%', height:'100%', objectFit:'cover' }}
                        onClick={() => window.open(`${photo.file_url}`,'_blank')}/>
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!window.confirm('Remove this photo?')) return;
                          try {
                            await API.delete(`/csos/${id}/media/${photo.id}`);
                            setCso(c => ({ ...c, media: c.media.filter(m => m.id !== photo.id) }));
                            toast.success('Photo removed');
                          } catch (err) { toast.error('Failed to remove photo'); }
                        }}
                        style={{ position:'absolute', top:6, right:6, width:28, height:28, borderRadius:6, border:'none', background:'rgba(0,0,0,0.6)', color:'white', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
                        title="Remove photo">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
            }
          </div>
        )}

        {/* ── DOCUMENTS ── */}
        {activeTab==='documents' && (
          <div>
            {docs.length===0
              ? <div className="empty-state">
                  <div className="empty-state-icon"><FileText size={48} /></div>
                  <h3>No documents uploaded</h3>
                </div>
              : <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {docs.map(doc => (
                    <a key={doc.id} href={`${doc.file_url}`} target="_blank" rel="noreferrer"
                      className="card" style={{ padding:'14px 18px', display:'flex', alignItems:'center', gap:14, transition:'all 0.2s' }}
                      onMouseOver={e=>e.currentTarget.style.background='var(--gray-50)'}
                      onMouseOut={e=>e.currentTarget.style.background='white'}>
                      <FileText size={24} />
                      <div>
                        <div style={{ fontWeight:600, fontSize:'0.85rem' }}>{doc.title||doc.file_url.split('/').pop()}</div>
                        {doc.file_size_kb && <div style={{ fontSize:'0.75rem', color:'var(--gray-400)' }}>{(doc.file_size_kb/1024).toFixed(1)} MB</div>}
                      </div>
                      <span style={{ marginLeft:'auto', color:'var(--green)', fontWeight:600, fontSize:'0.82rem', display:'flex', alignItems:'center', gap:4 }}>
                        <Download size={16} /> Download
                      </span>
                    </a>
                  ))}
                </div>
            }
          </div>
        )}

        {/* ── CONTACTS ── */}
        {activeTab==='contacts' && (
          <div className="grid-2">
            <div className="card card-pad">
              <h3 style={{ fontSize:'0.9rem', fontWeight:700, marginBottom:14 }}>Organisation Contact</h3>
              <InfoRow icon={Phone} label="Primary Phone" value={cso.phone}/>
              <InfoRow icon={Phone} label="Secondary Phone" value={cso.phone2}/>
              <InfoRow icon={Mail} label="Primary Email" value={cso.email}/>
              <InfoRow icon={Mail} label="Secondary Email" value={cso.email2}/>
              <InfoRow icon={Globe} label="Website" value={cso.website}/>
              <InfoRow icon={MapPin} label="Physical Address" value={cso.physical_address}/>
              <InfoRow icon={MapPinned} label="Province" value={cso.province}/>
              <InfoRow icon={MapPin} label="District" value={cso.district}/>
              <InfoRow icon={Building2} label="Ward" value={cso.ward}/>
            </div>
            <div className="card card-pad">
              <h3 style={{ fontSize:'0.9rem', fontWeight:700, marginBottom:14 }}>Contact Person</h3>
              <InfoRow icon={UserCircle} label="Name" value={cso.contact_name}/>
              <InfoRow icon={Briefcase} label="Designation" value={cso.contact_designation}/>
              <InfoRow icon={Phone} label="Phone" value={cso.contact_phone}/>
              <InfoRow icon={Mail} label="Email" value={cso.contact_email}/>
            </div>
          </div>
        )}

        {/* ── BOARD ── */}
        {activeTab==='board' && (
          <div>
            {(!cso.directors||cso.directors.length===0)
              ? <div className="empty-state">
                  <div className="empty-state-icon"><Users size={48} /></div>
                  <h3>No director details</h3>
                </div>
              : <div className="grid-3">
                  {cso.directors.map((d,i) => (
                    <div key={i} className="card card-pad" style={{ textAlign:'center' }}>
                      <div style={{ width:52, height:52, borderRadius:'50%', background:'linear-gradient(135deg,var(--green),var(--green-dark))', display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontWeight:800, fontSize:'1.1rem', margin:'0 auto 12px' }}>
                        {(d.full_name||'?').charAt(0)}
                      </div>
                      <div style={{ fontWeight:700, fontSize:'0.9rem' }}>{d.full_name}</div>
                      <div style={{ fontSize:'0.75rem', color:'var(--gray-400)', marginTop:2 }}>{d.role||'Director'}</div>
                      {d.phone && <div style={{ fontSize:'0.78rem', color:'var(--gray-500)', marginTop:6, display:'flex', alignItems:'center', justifyContent:'center', gap:4 }}><Phone size={14} /> {d.phone}</div>}
                      {d.email && <div style={{ fontSize:'0.78rem', color:'var(--gray-500)', display:'flex', alignItems:'center', justifyContent:'center', gap:4 }}><Mail size={14} /> {d.email}</div>}
                    </div>
                  ))}
                </div>
            }
          </div>
        )}

        {/* ── FOCUS ── */}
        {activeTab==='focus' && (
          <div className="card card-pad">
            <h3 style={{ fontSize:'0.95rem', fontWeight:700, marginBottom:16 }}>Focus Areas & Clusters</h3>
            <div style={{ display:'flex', flexWrap:'wrap', gap:10, marginBottom:20 }}>
              {cso.cluster_ovc      && <div className="tag" style={{ padding:'8px 16px', fontSize:'0.85rem', display:'flex', alignItems:'center', gap:6 }}><Heart size={16} /> OVC</div>}
              {cso.cluster_kp       && <div className="tag" style={{ padding:'8px 16px', fontSize:'0.85rem', display:'flex', alignItems:'center', gap:6 }}><Users size={16} /> Key Populations</div>}
              {cso.cluster_health   && <div className="tag" style={{ padding:'8px 16px', fontSize:'0.85rem', display:'flex', alignItems:'center', gap:6 }}><Activity size={16} /> Health</div>}
              {cso.cluster_gender   && <div className="tag" style={{ padding:'8px 16px', fontSize:'0.85rem', display:'flex', alignItems:'center', gap:6 }}><Users size={16} /> Gender</div>}
              {cso.cluster_advocacy && <div className="tag" style={{ padding:'8px 16px', fontSize:'0.85rem', display:'flex', alignItems:'center', gap:6 }}><Target size={16} /> Advocacy</div>}
            </div>
            {cso.women_led && <div style={{ padding:'10px 14px', background:'#fce7f3', borderRadius:8, marginBottom:8, fontSize:'0.85rem', color:'#9d174d', display:'flex', alignItems:'center', gap:6 }}><Users size={16} /> Women-Led Organisation</div>}
            {cso.youth_led && <div style={{ padding:'10px 14px', background:'#fef3c7', borderRadius:8, fontSize:'0.85rem', color:'#92400e', display:'flex', alignItems:'center', gap:6 }}><Users size={16} /> Youth-Led Organisation</div>}
          </div>
        )}
      </div>
    </div>
  );
}